import * as React from 'react';
import styled, {css} from 'styled-components';
import {useRoute} from 'react-router5';
import {lighten, transparentize} from 'polished';
import SlideDown from 'react-slidedown';
import SidebarContext from 'components/Sidebar/SidebarContext';
import ControlButton, {
  Container as ControlContainer,
} from './components/ControlButton';
import outline from 'helpers/outline';
import {IconChevronBottom} from 'icons';
import {RouteName} from "router/routes";
import {navigateTo} from "helpers/navigate-to";
import {Theme} from 'stores/AppStore';

export interface MenuItemProps {
  routeName?: RouteName | null;
  href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
  label: string;
  icon?: JSX.Element;
  controls?: JSX.Element | JSX.Element[];
  children?:
    | React.ComponentElement<MenuItemProps, any>
    | React.ComponentElement<MenuItemProps, any>[];
}

const Wrapper = styled.a<{
  isActive: boolean;
  isToggle?: boolean;
}>`
  position: relative;
  ${({isActive, isToggle, theme}) => isActive && css`
    &:before {
      content: "";
      
      position: absolute;
      top: 0;
      bottom: 0;
      left: ${isToggle ? '-12px' : '-8px'};

      margin: auto;
      border-radius: 0 5px 5px 0;
      background-color: ${theme.colors.primary};
      display: block;
      height: 30px;
      width: 4px;
    }
  `};
`;

const Content = styled.div<{
  isToggle?: boolean;
  isSidebarToggle?: boolean;
}>`
  border-radius: 6px;
  box-sizing: border-box;
  align-items: center;
  display: flex;
  
  ${({isSidebarToggle, isToggle}) => isSidebarToggle && isToggle && css`
    border-radius: 6px 6px 0 0;
    background-color: ${({theme}) => `
      ${theme.variables.backgroundColor};
    `};
  `};
  
  &:hover,
  &:focus-visible {
    background-color: ${({theme}) => `
      ${theme.variables.backgroundColor};
    `};
  }
`;

const Link = styled.a<{
  isToggle?: boolean;
}>`
  width: 100%;
  min-height: ${({isToggle}) => !isToggle && '58px'};
  padding: ${({isToggle}) => isToggle && '19px 10px'};
  border-radius: 6px;
  align-items: center;
  justify-content: ${({isToggle}) => !isToggle && 'center'};
  flex-direction: ${({isToggle}) => isToggle ? 'row' : 'column'};
  display: flex;
  cursor: pointer;
  
  &:focus-visible {
    ${outline(6, 2)};
  }
`;

const Icon = styled.div<{
  isToggle?: boolean;
}>`
  display: grid;
  margin-right: ${({isToggle}) => isToggle && '14px'};
  ${({theme}) => {
    switch (theme.name) {
      case Theme.Dark: return css`
        color: ${({theme}) => `
          ${transparentize(.2, theme.colors.white)};
        `}; 
      `;
      case Theme.Light: return css`
        color: ${({theme}) => `
          ${transparentize(.2, theme.variables.color)};
        `}; 
      `;
    }
  }};
`;

const Label = styled.span<{
  isToggle?: boolean;
}>`
  font-size: ${({isToggle}) => isToggle ? '14px' : '9px'};
  font-weight: 500;
  line-height: 18px;
`;

const Menu = styled.div`
  gap: 10px;
  display: grid;
  
  padding: 10px 0 10px 10px;
  box-sizing: border-box;
  border-radius: 0 0 6px 6px;
  background-color: ${({theme}) => `
    ${theme.variables.backgroundColor};
  `};
  
  ${Link} {
    padding-top: 13px;
    padding-bottom: 13px;
    padding-left: 34px;
    
    &:hover,
    &:focus-visible {
      background-color: ${({theme}) => `
        ${theme.variables.accentBackgroundColor};
      `};
    }
    
    &:active {
      background-color: ${({theme}) => `
        ${lighten(.03, theme.variables.accentBackgroundColor)};
      `};
    }
  }
`;

const Controls = styled.div`
  box-sizing: border-box;
  padding: 0 6px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const ToggleControl = styled.div<{
  isToggle?: boolean;
}>`
  ${({isToggle}) => isToggle && css`
    ${ControlContainer} {
      transform: rotate(-180deg);
    }
  `}
`;

/**
 * This component renders the menu item.
 * @param {MenuItemProps} props
 * @return {React.ReactElement<MenuItemProps>}
 * @constructor
 */
export default function MenuItem(
    props: MenuItemProps,
): React.ReactElement<MenuItemProps> {
  const context = React.useContext(SidebarContext);
  const {route: {name}} = useRoute();

  const [
    isToggle,
    setToggle,
  ] = React.useState<boolean>(false);

  function handleNavigate(e: any) {
    e.preventDefault();
    props?.routeName && navigateTo(props.routeName)
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render icon.
     * @return {JSX.Element}
     */
    function renderIcon(): JSX.Element | undefined {
      return props.icon ? (
        <Icon isToggle={context?.isToggle}>
          {props.icon}
        </Icon>
      ) : undefined;
    }

    /**
     * Render label.
     * @return {JSX.Element}
     */
    function renderLabel(): JSX.Element {
      return (
        <Label isToggle={context?.isToggle}>
          {props.label}
        </Label>
      );
    }

    /**
     * Render controls.
     * @return {JSX.Element | undefined}
     */
    function renderControls(): JSX.Element | undefined {
      /**
       * Render toggle control.
       * @return {JSX.Element}
       */
      function renderToggle(): JSX.Element | undefined {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          setToggle(!isToggle);
        }

        return props.children ? (
          <ToggleControl isToggle={isToggle} onClick={handleClick}>
            <ControlButton routeName={'#'}>
              <IconChevronBottom />
            </ControlButton>
          </ToggleControl>
        ) : undefined;
      }

      return context?.isToggle ? (
        <Controls>
          {renderToggle()}
          {props.controls && props.controls}
        </Controls>
      ) : undefined;
    }

    return (
      <Content isToggle={isToggle} isSidebarToggle={context?.isToggle}>
        <Link
          href={props.href}
          onClick={handleNavigate}
          isToggle={context?.isToggle}
        >
          {renderIcon()}
          {renderLabel()}
        </Link>
        {renderControls()}
      </Content>
    );
  }

  /**
   * Render menu.
   * @return {JSX.Element}
   */
  function renderMenu(): JSX.Element | undefined {
    return (props.children && context?.isToggle) ? (
      <SlideDown>
        {isToggle && (
          <Menu>
            {Array.isArray(props.children) ? (
              props.children.map((menuItem) => (
                menuItem
              ))
            ) : props.children}
          </Menu>
        )}
      </SlideDown>
    ) : undefined;
  }

  return (
    <Wrapper isToggle={context?.isToggle} isActive={props.routeName === name}>
      {renderContent()}
      {renderMenu()}
    </Wrapper>
  );
}
